// src/components/MenuList.js - Merged Production Logic with UI/UX Improvements & Accordion Behavior
import React, { useState, useEffect } from 'react';
import { Menu, Divider, Badge } from 'antd';
import {
    SearchOutlined,
    ReadOutlined,
    SettingOutlined,
    UserOutlined,
    FileTextOutlined,
    CreditCardOutlined,
    TeamOutlined,
    ShopOutlined,
    AppstoreOutlined,
    PieChartOutlined,
    AuditOutlined,
    FileAddOutlined,
    SnippetsOutlined,
    BookOutlined,
    WalletOutlined,
    FundProjectionScreenOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './MenuList.css';
import { useAuth } from '../context/AuthContext.js';
import { useNewFeatures } from '../hooks/useNewFeatures';
import { ROLES, isAdminRole } from '../permissions.js';

const MenuList = ({ collapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth();
    const { hasNewFeatures, newFeaturesCount } = useNewFeatures();
    const isAdmin = isAdminRole(user?.role);

    // State for selected menu item
    const [selectedKey, setSelectedKey] = useState(location.pathname);

    // State for currently open submenu keys (for accordion behavior)
    // Initialize with an empty array or the key of a submenu you want open by default
    const [openKeys, setOpenKeys] = useState([]);

    // Update selectedKey when the path changes
    useEffect(() => {
        setSelectedKey(location.pathname);

        // Additionally, for accordion: If the current path is a child of a submenu,
        // ensure that submenu is open.
        // We need to map the current path back to its parent submenu key.
        const allKeys = menuItems.flatMap(item => {
            if (item.children) {
                return [item.key, ...item.children.map(child => child.key)];
            }
            return [item.key];
        });

        // Find the parent key if the current path is a child
        let currentParentKey = null;
        for (const item of menuItems) {
            if (item.children && item.children.some(child => child.key === location.pathname)) {
                currentParentKey = item.key;
                break;
            }
        }

        // If current path belongs to a submenu, open that submenu
        if (currentParentKey && !openKeys.includes(currentParentKey)) {
            setOpenKeys([currentParentKey]); // Only open the current one for accordion behavior
        } else if (!currentParentKey && openKeys.length > 0 && !menuItems.find(item => item.key === location.pathname)) {
            // If navigated to a top-level item and no submenu is current, close all submenus
            setOpenKeys([]);
        }

    }, [location.pathname]); // Dependency array: re-run when location.pathname changes

    // Handler for the "Close Session" click
    const handleLogout = () => {
        console.log("Logout menu item clicked.");
        logout();
    };

    const userNameToDisplay = user?.firstName ?? 'Usuario';

    // Define the menu items array with specific keys.
    // Admin-only sections are conditionally spread in based on the isAdmin flag.
    const menuItems = [
        {
            key: "userSession",
            icon: <UserOutlined />,
            label: (
                <>
                    <span>{userNameToDisplay}</span>
                </>
            ),
            children: [
                {
                    key: "logout",
                    label: "Cerrar Sesión",
                    onClick: handleLogout,
                },
            ],
        },
        {
            key: "/Buscador",
            icon: <SearchOutlined />,
            label: <strong>Buscador</strong>,
        },

        // ── Admin-only: Dashboard, Caja ───────────────────────────────────
        ...(isAdmin ? [
            {
                key: "/Dashboard",
                icon: <PieChartOutlined />,
                label: (
                    <span style={{ position: 'relative', display: 'inline-block' }}>
                        Dashboard
                        {hasNewFeatures && (
                            <sup style={{ 
                                position: 'absolute', 
                                top: 0, 
                                right: -24,
                                backgroundColor: '#52c41a',
                                color: '#fff',
                                borderRadius: '10px',
                                padding: '0 6px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                lineHeight: '18px',
                                minWidth: '18px',
                                textAlign: 'center'
                            }}>
                                {newFeaturesCount}
                            </sup>
                        )}
                    </span>
                ),
            },
            { type: 'divider' },
            {
                key: "/Caja",
                icon: <AppstoreOutlined />,
                label: "Caja",
            },
        ] : []),

        // ── Ventas & Clientes — available to all roles ────────────────────
        {
            key: "ventasSubMenu",
            icon: <ShopOutlined />,
            label: "Ventas",
            children: [
                { key: "/Invoices", label: "Facturas", icon: <FileTextOutlined /> },
                { key: "/FacturaEnCola", label: "Facturas en Cola", icon: <WalletOutlined /> },
                { key: "/Presupuestos", label: "Presupuestos", icon: <SnippetsOutlined /> },
                { key: "/NotasCredito", label: "Notas de Crédito", icon: <CreditCardOutlined /> },
            ],
        },
        {
            key: "clientesSubMenu",
            icon: <TeamOutlined />,
            label: "Clientes",
            children: [
                { key: "/Clientes?tab=clientes", label: "Clientes", icon: <TeamOutlined /> },
                { key: "/CuentasCorrientes", label: "Ctas Corrientes", icon: <CreditCardOutlined /> },
                { key: "/CuentasAbiertas", label: "Ctas Abiertas", icon: <WalletOutlined /> },
            ],
        },

        // ── Admin-only: Compras, Productos, Ajustes ───────────────────────
        ...(isAdmin ? [
            {
                key: "comprasSubMenu",
                icon: <AuditOutlined />,
                label: "Compras",
                children: [
                    { key: "/OrdenesCompra", label: "Ordenes", icon: <SnippetsOutlined /> },
                    { key: "/Proveedores", label: "Proveedores", icon: <BookOutlined /> },
                ],
            },
            { type: 'divider' },
            {
                key: "productosSubMenu",
                icon: <FileAddOutlined />,
                label: "Productos",
                children: [
                    { key: "/PackProducts", label: "Stock" },
                    { key: "/ProductEditor", label: "Editor Manual" },
                    { key: "/Database", label: "Cargar Listas" },
                    ...(process.env.REACT_APP_DEMO_MODE === 'true' ? [] : [
                        { key: "/Stickers", label: "Etiquetas" },
                        { key: "/ProductAudit", label: "Papelera" },
                    ]),
                ],
            },
            { type: 'divider' },
            {
                key: "ajustesSubMenu",
                icon: <SettingOutlined />,
                label: "Ajustes",
                children: [
                    { key: "/Variables", label: "Variables" },
                ],
            },
            ...(user?.signageEnabled ? [
                { type: 'divider' },
                {
                    key: "/signage",
                    icon: <FundProjectionScreenOutlined />,
                    label: "Cartelería",
                },
            ] : []),
        ] : []),

        // ── Manuales — available to all roles ─────────────────────────────
        ...(process.env.REACT_APP_DEMO_MODE === 'true' ? [] : [
            { type: 'divider' },
            {
                key: "/manual",
                icon: <ReadOutlined />,
                label: "Manuales",
                children: [
                    { key: "/ClientDocumentation", label: "Documentación" },
                    { key: "/UserManual", label: "Manual de Usuario" },
                ],
            },
        ]),
    ];

    // Handler for when submenus are opened/closed
    // This is crucial for managing the openKeys state for accordion behavior.
    const onOpenChange = (keys) => {
        // 'keys' is an array of all currently open submenu keys.
        // For accordion behavior, we typically only want the last one to be in the array.
        // This logic ensures only one submenu is open at a time.
        const latestOpenKey = keys.find(key => !openKeys.includes(key));
        if (latestOpenKey) {
            setOpenKeys([latestOpenKey]);
        } else {
            // If a submenu was closed, the latestOpenKey will be undefined, so clear openKeys.
            setOpenKeys([]);
        }
    };


    return (
        <Menu
            theme={'light'}
            mode="inline"
            className="menu-bar"
            selectedKeys={[selectedKey]}
            // Control open submenus
            openKeys={openKeys}
            onOpenChange={onOpenChange} // This handler is essential for accordion
            items={menuItems}
            onClick={({ key }) => {
                // Only navigate if the key is a page route and not a submenu key or logout
                // Submenu parent keys should not trigger navigation
                if (key !== 'logout' && 
                    key !== 'userSession' &&
                    key !== 'ventasSubMenu' && 
                    key !== 'clientesSubMenu' &&
                    key !== 'comprasSubMenu' &&
                    key !== 'productosSubMenu' &&
                    key !== 'ajustesSubMenu') {
                    navigate(key);
                }
            }}
        />
    );
};

export default MenuList;
